import FormatDateTime from 'components/utils/Formatters/FormatDateTime'
import FormatDecimal from 'components/utils/Formatters/FormatDecimal'
import { useAppSelector } from 'hooks/reduxHooks'
import useLoyaltyUserBenefitView from 'loyalty/hooks/useLoyaltyUserBenefitView'
import getLoyaltyDisplayTier from 'loyalty/selectors/getLoyaltyDisplayTier'
import getLoyaltyIsInfiniteAccountTier from 'loyalty/selectors/getLoyaltyIsInfiniteAccountTier'
import type { ComponentProps } from 'react'
import * as React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import LoyaltyTierBenefitMessageBanner from './LoyaltyTierBenefitMessageBanner'
import getIsLoyaltyLeadRegion from 'loyalty/selectors/getIsLoyaltyLeadRegion'
import FormatCurrency from 'components/Common/FormatCurrency'

type InheritedLoyaltyTierBenefitMessageBannerProps = Pick<ComponentProps<typeof LoyaltyTierBenefitMessageBanner>, 'flat' | 'icon'>

interface Props extends InheritedLoyaltyTierBenefitMessageBannerProps {
  benefitType: App.LoyaltyBenefitType
  /**
   * @default benefit's availability
   */
  benefitAvailability?: number
}

function LoyaltyTierBenefitAvailabilityMessageBanner({
  benefitType,
  benefitAvailability,
  ...rest
}: Props) {
  const intl = useIntl()
  const displayTier = useAppSelector(getLoyaltyDisplayTier)
  const isInfiniteTier = useAppSelector(getLoyaltyIsInfiniteAccountTier)
  const statusReviewDate = useAppSelector(state => state.loyalty.account.data?.statusReviewDate)
  const benefitView = useLoyaltyUserBenefitView(benefitType)
  const isLeadLoyaltyRegion = useAppSelector(getIsLoyaltyLeadRegion)

  if (!benefitView) return null

  const availability = benefitAvailability ?? benefitView.availability

  if (!availability) return null

  const hasVoucherAmount = (benefitType === 'travelshoot_credit' || benefitType === 'tour_exp_credit')
    && !!benefitView.benefit.value?.amount

  let availabilityMessage: React.ReactNode
  if (benefitType === 'discounted_insurance') {
    availabilityMessage = intl.formatMessage(
      {
        id: 'dsWo-ZHt6rFckLcLGqAf',
        defaultMessage: 'Available to redeem <b>{amount}% off</b>',
        description: 'Loyalty benefit availability for discounted insurance showing percentage discount',
      },
      {
        amount: benefitView.benefit.value?.amount ?? 0,
        b: (chunks: React.ReactNode) => <b>{chunks}</b>,
      },
    )
  } else if (availability === Infinity) {
    availabilityMessage = intl.formatMessage(
      {
        id: '-FuKz4_bk2uPoEcAaKjk',
        defaultMessage: '<b>Unlimited</b> redemptions available',
        description: 'Loyalty benefit availability shown when the benefit can be redeemed an unlimited number of times',
      },
      { b: (chunks: React.ReactNode) => <b>{chunks}</b> },
    )
  } else if (hasVoucherAmount && isLeadLoyaltyRegion) {
    const amount = benefitView.benefit.value?.amount ?? 0
    const voucherAvailabilityLabel = intl.formatMessage(
      {
        id: 'j31tiZGaOk7XMUEeJJuL',
        defaultMessage: 'x{count} {amount} {count, plural, one {voucher} other {vouchers}}',
        description: 'Voucher availability label with count and value in the loyalty benefits section',
      },
      {
        count: availability,
        amount: <FormatCurrency value={amount} format="dynamic" />,
      },
    )
    availabilityMessage = intl.formatMessage(
      {
        id: 'vnpuxR7daaEqixS7lm9S',

        defaultMessage: 'Available to redeem <b>{voucherAvailabilityLabel}</b>',
        description: 'Loyalty benefit availability for voucher type in lead region showing count and amount',
      },
      {
        voucherAvailabilityLabel,
        b: (chunks: React.ReactNode) => <b>{chunks}</b>,
      },
    )
  } else if (hasVoucherAmount) {
    const voucherAvailabilityLabel = intl.formatMessage(
      {
        id: '4OfTx_ccwgKrTGTtb0X2',
        defaultMessage: 'x{count, plural, one {# voucher} other {# vouchers}}',
        description: 'Voucher availability label with count in the loyalty benefits section',
      },
      { count: availability },
    )
    availabilityMessage = intl.formatMessage(
      {
        id: 'fooqhuqFXCbbuOes3GhR',
        defaultMessage: 'Available to redeem <b>{voucherAvailabilityLabel}</b>',
        description: 'Loyalty benefit availability for voucher type in non-lead region showing count',
      },
      {
        voucherAvailabilityLabel,
        b: (chunks: React.ReactNode) => <b>{chunks}</b>,
      },
    )
  } else {
    availabilityMessage = intl.formatMessage(
      {
        id: 'dK2zRvXjBtwTS3jc_IrS',
        defaultMessage: 'Available to redeem <b>x{count}</b>',
        description: 'Loyalty benefit availability showing multiplier count',
      },
      {
        count: <FormatDecimal value={availability} />,
        b: (chunks: React.ReactNode) => <b>{chunks}</b>,
      },
    )
  }

  return <LoyaltyTierBenefitMessageBanner
    tier={displayTier}
    benefitType={benefitType}
    density="compact"
    endContent={!isInfiniteTier && !!statusReviewDate && <div>
      <FormattedMessage
        id="RmXB7STXZan4tw09nZ_Q"
        defaultMessage="Expires <bold>{date}</bold>"
        description="Expiry date label for loyalty benefit availability"
        values={{
          date: <FormatDateTime value={statusReviewDate} month="short" year="numeric" />,
          bold: (chunks) => <b>{chunks}</b>,
        }}
      />
    </div>}
    {...rest}
  >
    {availabilityMessage}
  </LoyaltyTierBenefitMessageBanner>
}

export default LoyaltyTierBenefitAvailabilityMessageBanner
